import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Building2, Globe, Users } from 'lucide-react';
import { useOrganizations, useCreateOrganization } from '@/hooks/useOrganizations';
import {
  Button,
  Card,
  EmptyState,
  SearchInput,
  Modal,
  Input,
  Select,
  PageHeader,
  Skeleton,
} from '@solid/ui';
import { Badge } from '@/components/ui/Badge';
import type { OrgSize } from '@/types';

const sizeLabels: Record<OrgSize, string> = {
  SOLO: '1 person',
  SMALL: '2-10',
  MEDIUM: '11-50',
  LARGE: '51-200',
  ENTERPRISE: '200+',
};

export default function Organizations() {
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);

  const { data, isLoading } = useOrganizations({
    search: search || undefined,
  });

  const orgs = data?.data ?? [];
  const total = data?.total ?? 0;

  return (
    <div className="page-enter">
      <PageHeader
        title="Organizations"
        subtitle={`${total} organizations`}
        actions={
          <Button onClick={() => setShowCreate(true)}>
            <Plus className="w-4 h-4" />
            Add Organization
          </Button>
        }
      />

      <div className="flex items-center gap-3 mb-6">
        <SearchInput
          searches="organizations"
          value={search}
          onChange={setSearch}
          className="w-80"
        />
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="card p-4">
              <Skeleton className="h-5 w-48" />
            </div>
          ))}
        </div>
      ) : orgs.length === 0 ? (
        <Card padding="md">
          <EmptyState
            icon={<Building2 />}
            title="No organizations yet"
            description="Add your first organization to group contacts together."
            action={
              <Button onClick={() => setShowCreate(true)}>
                <Plus className="w-4 h-4" />
                Add Organization
              </Button>
            }
          />
        </Card>
      ) : (
        <Card padding="none">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-grey-200">
                  <th className="text-left text-xs font-semibold text-grey-500 uppercase tracking-wider px-4 py-3">Name</th>
                  <th className="text-left text-xs font-semibold text-grey-500 uppercase tracking-wider px-4 py-3">Domain</th>
                  <th className="text-left text-xs font-semibold text-grey-500 uppercase tracking-wider px-4 py-3">Industry</th>
                  <th className="text-left text-xs font-semibold text-grey-500 uppercase tracking-wider px-4 py-3">Size</th>
                  <th className="text-left text-xs font-semibold text-grey-500 uppercase tracking-wider px-4 py-3">Location</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-grey-100">
                {orgs.map((org) => (
                  <tr key={org.id} className="hover:bg-grey-50 transition-colors">
                    <td className="px-4 py-3">
                      <Link
                        to={`/admin/organizations/${org.id}`}
                        className="flex items-center gap-2 font-medium text-secondary hover:text-primary transition-colors"
                      >
                        <Building2 className="w-4 h-4 text-grey-400" />
                        {org.name}
                      </Link>
                    </td>
                    <td className="px-4 py-3">
                      {org.domain ? (
                        <span className="flex items-center gap-1.5 text-sm text-grey-600">
                          <Globe className="w-3.5 h-3.5 text-grey-400" />
                          {org.domain}
                        </span>
                      ) : (
                        <span className="text-sm text-grey-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-grey-600">
                        {org.industry || '-'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {org.size ? (
                        <Badge variant="outline">
                          <Users className="w-3 h-3 mr-1" />
                          {sizeLabels[org.size]}
                        </Badge>
                      ) : (
                        <span className="text-sm text-grey-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-grey-600">
                        {[org.city, org.state].filter(Boolean).join(', ') || '-'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <CreateOrgDialog open={showCreate} onOpenChange={setShowCreate} />
    </div>
  );
}

function CreateOrgDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [form, setForm] = useState({
    name: '',
    domain: '',
    website: '',
    industry: '',
    size: '',
    city: '',
    state: '',
  });
  const createOrg = useCreateOrganization();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await createOrg.mutateAsync({
      name: form.name,
      domain: form.domain || undefined,
      website: form.website || undefined,
      industry: form.industry || undefined,
      size: form.size || undefined,
      city: form.city || undefined,
      state: form.state || undefined,
    });
    onOpenChange(false);
    setForm({ name: '', domain: '', website: '', industry: '', size: '', city: '', state: '' });
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Add Organization"
      description="Create a new organization."
      footer={
        <>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="submit" form="create-organization-form" loading={createOrg.isPending}>
            Create Organization
          </Button>
        </>
      }
    >
      <form id="create-organization-form" onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Name"
          required
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Domain"
            placeholder="example.com"
            value={form.domain}
            onChange={(e) => setForm({ ...form, domain: e.target.value })}
          />
          <Input
            label="Website"
            placeholder="https://example.com"
            value={form.website}
            onChange={(e) => setForm({ ...form, website: e.target.value })}
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Industry"
            value={form.industry}
            onChange={(e) => setForm({ ...form, industry: e.target.value })}
          />
          <Select
            label="Company Size"
            value={form.size}
            onChange={(e) => setForm({ ...form, size: e.target.value })}
            options={[
              { value: '', label: 'Select size' },
              { value: 'SOLO', label: 'Solo (1)' },
              { value: 'SMALL', label: 'Small (2-10)' },
              { value: 'MEDIUM', label: 'Medium (11-50)' },
              { value: 'LARGE', label: 'Large (51-200)' },
              { value: 'ENTERPRISE', label: 'Enterprise (200+)' },
            ]}
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="City"
            value={form.city}
            onChange={(e) => setForm({ ...form, city: e.target.value })}
          />
          <Input
            label="State"
            value={form.state}
            onChange={(e) => setForm({ ...form, state: e.target.value })}
          />
        </div>
      </form>
    </Modal>
  );
}
